/*
*  logoPresets.js
*  Project: Kanoogi Platform - Shared KAN_Logo3D option presets
*
*  One source of truth for "Kanoogi 3D logo, no spin, About-style lighting"
*  so the About hero and any other hero-style placement (e.g. the /finish
*  welcome) render an identical treatment without copy-pasting the closure.
*  Diverge them later by destructuring the returned object.
*
*  Created by Chris Taylor on May 27, 2026
*  Copyright 2026 Kanoogi Inc. | All Rights Reserved.
*
*/

"use strict";

/* ---------------------------------------------------------------- */

// AboutStyleOptions() - The opts bundle for the About-style hero treatment:
//   - No fly-in / no click-to-spin (intro:'static', baseMode:'static').
//     The renderer still runs the idle Y/X rocking wobble — that's a base
//     behaviour of the static mode, not a separate animation.
//   - Warm lit-room lighting with a tightened point-light falloff
//     (pointLightFalloff bumped 5x from the renderer default) so the
//     swipe reads as a tighter, more flashlight-like beam against the
//     mostly-flat front face.
//   - Per-frame closure that breathes the base lz with a slow cosine
//     PLUS strafes an additive red/blue/green point light across the
//     front face every 3-7s. Each call returns a FRESH closure so two
//     simultaneous logos don't share swipe timing.
//
// Returns a new opts object every call so callers can mutate without
// poisoning the shared preset.

export function AboutStyleOptions()
	{
	return {
		intro:'static',
		baseMode:'static',
		lighting:{lx:0.4,ly:0.6,lz:-1.0,ambient:0.5,specStrength:0.25,shininess:32,pointLightFalloff:0.00025},
		lightingAnimate:(()=>
			{
			const COLORS=[										// cycle each swipe — red → blue → green → red ...
				[1.0,0.1,0.1],									// red
				[0.1,0.3,1.0],									// blue
				[0.2,1.0,0.2],									// green
				];
			let nextSwipeAt=2+Math.random()*3;					// first whip 2-5s in
			let swipeStart=-1;									// -1 = idle
			let swipeAngle=0;									// random per swipe
			let swipeCount=0;									// indexes into COLORS
			let swipeColor=COLORS[0];							// latched at swipe start, held for the whole whip
			const SWIPE_DUR=4.0;								// slower, more drawn-out pass
			const PEAK_INTENSITY=7.0;							// brighter to compensate tighter falloff
			return (t,L)=>
				{
				L.lz=-Math.cos(t*Math.PI/4);					// base breathing pulse continues
				if (swipeStart<0&&t>=nextSwipeAt)				// schedule fires
					{
					swipeStart=t;
					swipeAngle=Math.random()*Math.PI*2;
					swipeColor=COLORS[swipeCount++%COLORS.length];
					}
				if (swipeStart>=0)
					{
					const u=(t-swipeStart)/SWIPE_DUR;
					if (u<1&&L.meshBbox)
						{
						const bb=L.meshBbox;
						const cx=(bb.min[0]+bb.max[0])/2;
						const cy=(bb.min[1]+bb.max[1])/2;
						const hw=(bb.max[0]-bb.min[0])/2;
						const hh=(bb.max[1]-bb.min[1])/2;
						const k=1-2*u;							// +1 → -1: sweep direction
						L.pointLightPos=[
							cx+Math.cos(swipeAngle)*hw*1.5*k,	// 1.5x half-width overshoot
							cy+Math.sin(swipeAngle)*hh*1.5*k,
							bb.max[2]+hw*0.12,					// closer to front face → tighter spot
							];
						L.pointLightIntensity=PEAK_INTENSITY*Math.sin(u*Math.PI);	// bell 0 → peak → 0
						L.pointLightColor=swipeColor;
						return;
						}
					swipeStart=-1;								// swipe done, schedule next
					L.pointLightIntensity=0;
					nextSwipeAt=t+3+Math.random()*4;
					}
				};
			})()
		};
	}
